// src/core/noseProfile.js
//
// Per-user nose depth (roadmap item 2).
//
// Every surface the engine seats and occludes against is the MediaPipe
// CANONICAL face — an average. A user with a more prominent nose has real skin
// in front of the canonical nose (frame sinks in, real nose shows through the
// far lens at yaw); a flatter nose leaves the frame floating. This module
// measures the user's nose prominence and produces a warped copy of the
// canonical vertices whose NOSE REGION is scaled along +Z about the eye line.
//
// Measurement: MediaPipe landmark z is a relative depth in the same units as x
// (normalised by image width, origin near the head centre). On a frontal frame,
//   prominence_norm = z(eye line) − z(nose tip)       (tip is closer → smaller z)
//   cm per unit     = canonical outer-canthal distance (8.89 cm) / |x33 − x263|
//   r = prominence_cm / canonical prominence (4.3 cm)
// r is averaged over frontal frames, clamped, then frozen.
//
// Applying r: for canonical vertex i with nose weight m_i (1 at the nose ridge,
// fading to 0 at the cheeks/brow):  z' = zEye + (z − zEye) · (1 + (r − 1) · m_i)

import { CANONICAL_FACE_VERTICES } from '../web/canonicalFace.js';

const N = 468;
const cv = CANONICAL_FACE_VERTICES;

// Eye line depth: mean z of the four eye corners (33, 133, 362, 263).
export const EYE_LINE_Z = (cv[33*3+2] + cv[133*3+2] + cv[362*3+2] + cv[263*3+2]) / 4;
export const CANONICAL_NOSE_PROMINENCE = cv[1*3+2] - EYE_LINE_Z;   // nose tip (1) − eye line
export const CANONICAL_OUTER_CANTHAL = Math.abs(cv[33*3] - cv[263*3]);

// Smooth nose mask over canonical (x, y): full weight on the ridge, fading out
// sideways over the nose wings and vertically at the brow / upper lip.
export const NOSE_MASK = new Float32Array(N);
for (let i = 0; i < N; i++) {
  const x = Math.abs(cv[i*3]), y = cv[i*3+1];
  const wx = clamp01(1 - (x - 1.4) / 1.6);          // 1 until |x|=1.4, 0 at |x|=3.0
  const wy = y > 3.6 ? clamp01(1 - (y - 3.6) / 1.4)  // fade above the bridge (3.27) to the brow
           : y < -2.2 ? clamp01(1 - (-2.2 - y) / 1.2) // fade below the nose base to the lip
           : 1;
  NOSE_MASK[i] = wx * wy;
}
function clamp01(v) { return Math.max(0, Math.min(1, v)); }

/** Warped canonical vertices for nose scale r (new Float32Array). r=1 → copy. */
export function warpedCanonical(r) {
  const out = new Float32Array(cv.length);
  for (let i = 0; i < N; i++) {
    out[i*3] = cv[i*3]; out[i*3+1] = cv[i*3+1];
    const z = cv[i*3+2];
    out[i*3+2] = EYE_LINE_Z + (z - EYE_LINE_Z) * (1 + (r - 1) * NOSE_MASK[i]);
  }
  return out;
}

/** Warped z for a single canonical vertex index. */
export function warpedZ(i, r) {
  return EYE_LINE_Z + (cv[i*3+2] - EYE_LINE_Z) * (1 + (r - 1) * NOSE_MASK[i]);
}

/**
 * Estimate the user's nose scale r from one frontal frame's landmarks.
 * Returns null if the frame isn't usable.
 */
export function measureNoseScale(landmarks) {
  if (!landmarks || landmarks.length < N) return null;
  const l33 = landmarks[33], l263 = landmarks[263], l133 = landmarks[133], l362 = landmarks[362], tip = landmarks[1];
  const canthal = Math.abs(l33.x - l263.x);
  if (canthal < 0.02) return null;
  const eyeZ = (l33.z + l133.z + l362.z + l263.z) / 4;
  const promNorm = eyeZ - tip.z;                      // > 0 when the tip is closer
  if (!(promNorm > 0)) return null;
  const promCm = promNorm / canthal * CANONICAL_OUTER_CANTHAL;
  return promCm / CANONICAL_NOSE_PROMINENCE;
}

/** Running estimator: EMA over frontal frames, frozen after `lockAfter` samples. */
export class NoseScaleEstimator {
  constructor({ lockAfter = 40, min = 0.7, max = 1.4 } = {}) {
    this.lockAfter = lockAfter; this.min = min; this.max = max;
    this.value = 1; this.samples = 0; this.locked = false;
  }
  push(landmarks) {
    if (this.locked) return false;
    const r = measureNoseScale(landmarks);
    if (r == null) return false;
    const c = Math.max(this.min, Math.min(this.max, r));
    this.value = this.samples === 0 ? c : this.value + (c - this.value) * 0.1;
    this.samples++;
    if (this.samples >= this.lockAfter) this.locked = true;
    return true;
  }
}
